import React, { useState, useEffect } from "react";
import { TuitionRequestInterface } from "../../../interfaces/tuitionRequest.interface";
import {
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import "../Request.scss";
import { get } from "../../../redux/ApiHelper";
import ApprovedRequestDetails from "./components/ApprovedRequestDetails";
import RequestForm from "./components/RequestForm";
import ParentShadowWalletDetails from "./components/ParentShadowWalletDetails";
import { useParams } from "react-router-dom";
import { ParentShadowWalletInterface } from "../../../interfaces/parent-shadow-wallet.interface";

const RequestDetails = () => {
  const { id } = useParams();

  const [requestDetails, setRequestDetails] =
    useState<TuitionRequestInterface>();
  const [walletDetails, setWalletDetails] =
    useState<ParentShadowWalletInterface | null>(null);
  const [loadingWallet, setLoadingWallet] = useState(false);

  useEffect(() => {
    if (id) {
      get(`/tuition/tuition-request-details/${id}`).then((response) => {
        setRequestDetails(response);
      });
    }
  }, [id]);

  useEffect(() => {
    if (!id) return;

    const fetchWalletDetails = async () => {
      setLoadingWallet(true);
      try {
        const response = await get(
          `/admin/tuition/parent-shadow-wallet/${id}`,
          {},
          { suppressNotFoundToast: true }
        );
        setWalletDetails(response);
      } catch {
        setWalletDetails(null);
      } finally {
        setLoadingWallet(false);
      }
    };

    fetchWalletDetails();
  }, [id]);

  if (!id) {
    return <div>No ID provided. Please select a request.</div>;
  }

  if (!requestDetails && !loadingWallet && !walletDetails) {
    return <div>No request found for the given ID.</div>;
  }

  return (
    <div className="flex-grow flex flex-col bg-primary-dark p-8 justify-center items-center overflow-hidden">
      <Paper className="p-8 w-full  rounded-lg shadow-lg flex-grow overflow-auto flex flex-col">
        <h2 className="text-2xl font-semibold text-primary-dark mb-6">
          Request Details
        </h2>

        <div className="space-y-4 my-4">
          {requestDetails && (
            <div className="flex justify-between gap-6">
              <div className="text-left space-y-2">
                <p className="text-gray-700">
                  <span className="text-gray-500 font-semibold">Name:</span>{" "}
                  <span className="text-black">
                    {requestDetails?.student?.name || "N/A"}
                  </span>
                </p>
                <p className="text-gray-700">
                  <span className="text-gray-500 font-semibold">Email:</span>{" "}
                  <span className="text-black">
                    {requestDetails?.parent?.email || "N/A"}
                  </span>
                </p>
                <p className="text-gray-700">
                  <span className="text-gray-500 font-semibold">Phone:</span>{" "}
                  <span className="text-black">
                    {`${requestDetails?.parent?.phoneCode || ""} ${requestDetails?.parent?.phoneNumber || ""
                      }`}
                  </span>
                </p>
                <p className="text-gray-700">
                  <span className="text-gray-500 font-semibold">Address:</span>{" "}
                  <span className="text-black">
                    {`${requestDetails?.parent?.address?.addressLine || ""}, ${requestDetails?.parent?.address?.city || ""
                      }, ${requestDetails?.parent?.address?.state || ""} - ${requestDetails?.parent?.address?.pinCode || ""
                      }`}
                  </span>
                </p>
              </div>
              <div className="text-left space-y-2">
                <p className="text-gray-700">
                  <span className="text-gray-500 font-semibold">
                    Required Subject(s):
                  </span>{" "}
                  <span className="text-black">
                    {requestDetails?.preferredSubjects
                      ? requestDetails?.preferredSubjects?.join(", ")
                      : "N/A"}
                  </span>
                </p>
                <p className="text-gray-700">
                  <span className="text-gray-500 font-semibold">
                    Preferred Tuition Hour:
                  </span>{" "}
                  <span className="text-black">
                    {requestDetails?.preferredHours || "N/A"}
                  </span>
                </p>
                <p className="text-gray-700">
                  <span className="text-gray-500 font-semibold">
                    Preferred Teacher Gender:
                  </span>{" "}
                  <span className="text-black">
                    {requestDetails?.preferredTeacherGender || "N/A"}
                  </span>
                </p>
                <p className="text-gray-700">
                  <span className="text-gray-500 font-semibold">
                    Preferred Teacher Experience:
                  </span>{" "}
                  <span className="text-black">
                    {requestDetails?.requiredTeacherExperience || "N/A"}
                  </span>
                </p>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <h3 className="font-semibold text-lg">Parent Shadow Wallet</h3>
            <ParentShadowWalletDetails
              wallet={walletDetails}
              loading={loadingWallet}
            />
          </div>

          {requestDetails && (
            <>
          {/* Display Student's Preferred Schedule */}
          <div className="space-y-2">
            <h3 className="font-semibold text-lg">
              Student's Preferred Schedule
            </h3>
            <div className="p-4 border border-yellow-400 rounded-lg shadow-sm">
              <Table className="request-detatls-table">
                <TableHead>
                  <TableRow>
                    <TableCell>Day</TableCell>
                    <TableCell>From</TableCell>
                    <TableCell>To</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {requestDetails?.preferredSchedule?.map(
                    ({ day, from, to }) => (
                      <TableRow key={day}>
                        <TableCell>{day}</TableCell>
                        <TableCell>{from}</TableCell>
                        <TableCell>{to}</TableCell>
                      </TableRow>
                    )
                  )}
                </TableBody>
              </Table>
            </div>
          </div>

          {requestDetails?.adminStatus === "approved" ? (
            <ApprovedRequestDetails
              tuitionRequest={requestDetails}
              wallet={walletDetails}
            />
          ) : (
            <RequestForm requestDetails={requestDetails} />
          )}
            </>
          )}
        </div>
      </Paper>
    </div>
  );
};

export default RequestDetails;
